// frontend/src/screens/admin/sps/CpiRatesScreen.jsx
import { useState } from 'react';
import { Container, Table, Button, Badge, Modal, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import {
  useGetCpiRatesQuery,
  useCreateCpiYearMutation,
  useUpdateCpiYearMutation,
  useExportCpiJsonMutation,
} from '../../../slices/spsApiSlices';

// Format rate as percentage string e.g. 0.028 → "2.800%"
const fmtRate = (r) => `${(Number(r) * 100).toFixed(3)}%`;

// ── Empty form state ──────────────────────────────────────────────────────────
const emptyForm = {
  year:          '',
  rate:          '',
  circularRef:   '',
  confirmedDate: '',
  isPending:     false,
  notes:         '',
};

export default function CpiRatesScreen() {
  const { data: rates = [], isLoading, isError, refetch } = useGetCpiRatesQuery();
  const [createCpi, { isLoading: creating }] = useCreateCpiYearMutation();
  const [updateCpi, { isLoading: updating }] = useUpdateCpiYearMutation();
  const [exportCpi, { isLoading: exporting }] = useExportCpiJsonMutation();

  const [showModal, setShowModal]   = useState(false);
  const [editYear,  setEditYear]    = useState(null);  // null = create new
  const [form,      setForm]        = useState(emptyForm);
  const [feedback,  setFeedback]    = useState(null);  // { type, msg }

  const openCreate = () => {
    setEditYear(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditYear(row.year);
    setForm({
      year:          row.year,
      rate:          String(row.rate),
      circularRef:   row.circularRef || '',
      confirmedDate: row.confirmedDate ? row.confirmedDate.substring(0, 10) : '',
      isPending:     row.isPending || false,
      notes:         row.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editYear !== null) {
        await updateCpi({ year: editYear, ...form, rate: Number(form.rate) }).unwrap();
        setFeedback({ type: 'success', msg: `Year ${editYear} updated` });
      } else {
        await createCpi({ ...form, year: Number(form.year), rate: Number(form.rate) }).unwrap();
        setFeedback({ type: 'success', msg: `Year ${form.year} created` });
      }
      setShowModal(false);
      refetch();
    } catch (err) {
      setFeedback({ type: 'danger', msg: err?.data?.message || 'Save failed' });
    }
  };

  const handleExport = async () => {
    try {
      const result = await exportCpi().unwrap();
      setFeedback({ type: 'success', msg: `✓ ${result.message} (${result.years} years)` });
    } catch (err) {
      setFeedback({ type: 'danger', msg: err?.data?.message || 'Export failed' });
    }
  };

  if (isLoading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  if (isError)   return <Alert variant="danger" className="m-3">Failed to load CPI rates. Check your connection.</Alert>;

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">CPI Rates</h4>
          <small className="text-muted">
            Annual Consumer Price Index uplift rates applied to referable amounts (DPER circulars)
          </small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? <Spinner size="sm" animation="border" /> : '⬇ Export to JSON'}
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            + Add Year
          </Button>
        </Col>
      </Row>

      {feedback && (
        <Alert
          variant={feedback.type}
          dismissible
          onClose={() => setFeedback(null)}
          className="mb-3"
        >
          {feedback.msg}
        </Alert>
      )}

      {/* Rates table */}
      <div className="table-responsive">
        <Table bordered hover size="sm" className="mb-0">
          <thead className="table-dark">
            <tr>
              <th>Year ending</th>
              <th>CPI Rate</th>
              <th>Circular Reference</th>
              <th>Confirmed Date</th>
              <th>Status</th>
              <th>Last Updated</th>
              <th style={{ width: 80 }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((row) => (
              <tr
                key={row.year}
                style={{ backgroundColor: row.isPending ? '#fff8e1' : undefined }}
              >
                <td className="fw-semibold">31/12/{row.year}</td>
                <td className="fw-bold" style={{ color: row.rate === 0 ? '#888' : '#145a32' }}>
                  {fmtRate(row.rate)}
                  {row.rate === 0 && <span className="text-muted ms-1 fw-normal">(no uplift)</span>}
                </td>
                <td>{row.circularRef || <span className="text-muted fst-italic">—</span>}</td>
                <td>
                  {row.confirmedDate
                    ? new Date(row.confirmedDate).toLocaleDateString('en-IE')
                    : <span className="text-muted fst-italic">—</span>}
                </td>
                <td>
                  {row.isPending ? (
                    <Badge bg="warning" text="dark">Pending</Badge>
                  ) : (
                    <Badge bg="success">Confirmed</Badge>
                  )}
                </td>
                <td>
                  <small className="text-muted">
                    {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString('en-IE') : '—'}
                  </small>
                </td>
                <td>
                  <Button variant="outline-primary" size="sm" onClick={() => openEdit(row)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="mt-2 text-muted" style={{ fontSize: '.8rem' }}>
        {rates.length} years loaded. Use <strong>Export to JSON</strong> before running the Python calculator to sync any changes.
      </div>

      {/* Edit / Create Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editYear !== null ? `Edit CPI Year ${editYear}` : 'Add New CPI Year'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {editYear === null && (
              <Form.Group className="mb-3">
                <Form.Label>Year <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  min={2014}
                  max={2100}
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  placeholder="e.g. 2026"
                />
                <Form.Text className="text-muted">
                  The calendar year-end this CPI rate applies to
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>CPI Rate <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                step="0.001"
                min="-0.1"
                max="0.5"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
                placeholder="e.g. 0.028 for 2.8%"
              />
              <Form.Text className="text-muted">
                Enter as decimal (0.028 = 2.8%). Enter 0 for a zero-inflation year.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>DPER Circular Reference</Form.Label>
              <Form.Control
                type="text"
                value={form.circularRef}
                onChange={(e) => setForm({ ...form, circularRef: e.target.value })}
                placeholder="e.g. DPER Circular 01/2023"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirmed Date</Form.Label>
              <Form.Control
                type="date"
                value={form.confirmedDate}
                onChange={(e) => setForm({ ...form, confirmedDate: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="isPending"
                label="Pending — rate not yet officially confirmed"
                checked={form.isPending}
                onChange={(e) => setForm({ ...form, isPending: e.target.checked })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional admin note"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={creating || updating || !form.rate}
          >
            {creating || updating ? <Spinner size="sm" animation="border" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}